import { makeAutoObservable, runInAction } from 'mobx';

import { UsersApi } from './users.api';
import { CreateUserRequestDto, UpdateUserRequestDto, UserPaginationItemDto, UserResponseDto } from './users.dto';
import { UsersPageFilters } from './users.types';
import { DEFAULT_USERS_TABLE_FILTERS, MAX_USERS_PER_PAGE } from './users.constants';

class UsersStore {
  usersApi: UsersApi;

  isLoading = false;

  isSendingInvitationLoading = false;

  users: UserPaginationItemDto[];

  filters: UsersPageFilters;

  totalUsers: number;

  isUserProfileLoading = false;

  user: UserResponseDto | null = null;

  constructor() {
    this.usersApi = new UsersApi();
    this.users = [];
    this.filters = DEFAULT_USERS_TABLE_FILTERS;
    this.totalUsers = 0;

    makeAutoObservable(this, {}, { autoBind: true });
  }

  public async getAllUsers() {
    this.isLoading = true;

    try {
      const response = await this.usersApi.getAllUsers({
        offset: this.filters.pageIndex * MAX_USERS_PER_PAGE,
        count: MAX_USERS_PER_PAGE,
        sortField: this.filters.sortField,
        sortDirection: this.filters.sortDirection,
      });

      runInAction(() => {
        this.users = response.items;
        this.totalUsers = response.total;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  public async getUserById(userId: number) {
    this.isUserProfileLoading = true;

    try {
      const response = await this.usersApi.getUserById(userId);

      runInAction(() => {
        this.user = response;
      });
    } finally {
      runInAction(() => {
        this.isUserProfileLoading = false;
      });
    }
  }

  public async createUser(user: CreateUserRequestDto) {
    await this.usersApi.createUser(user);
    await this.getAllUsers();
  }

  public async updateUser(userId: number, user: UpdateUserRequestDto) {
    await this.usersApi.update(userId, user);
    await this.getAllUsers();
  }

  public async deleteUser(userId: number) {
    await this.usersApi.delete(userId);
    this.updateFilters({ ...this.filters, pageIndex: 0 });
    await this.getAllUsers();
  }

  public async resendInvitation(userId: number) {
    try {
      this.isSendingInvitationLoading = true;
      await this.usersApi.resendInvitation(userId);
    } finally {
      runInAction(() => {
        this.isSendingInvitationLoading = false;
      });
    }
  }

  public updateFilters = (filters: UsersPageFilters) => {
    runInAction(() => {
      this.filters = filters;
    });
  };
}

export default UsersStore;
